import { useEffect, useState, type ReactNode } from 'react';
import { api, type FilaRanking, type RankingPuntos, type Yo } from '../services/api';
import { Aviso, Cargando, CoronaEl1, Tarjeta, Titulo, Vacio } from '../components/ui';
import { Mes } from './Mes';
import { Parejas } from './Parejas';
import { mesEnPalabras, plazoAutoconfirmar } from '../nucleo/fechas';
import { ESCALA, MESES_QUE_CUENTAN, PUNTOS_RESTO, RANKING_PUBLICO, veRanking } from '../nucleo/puntos';
import { DIAS_EN_CARRERA } from '../nucleo/ranking';

export type Solapa = 'general' | 'mes' | 'puntos' | 'parejas';

/**
 * Las tablas, en solapas.
 *
 * Van juntas y no como botones sueltos porque contestan la misma pregunta con
 * distinto plazo: "quién anduvo mejor este mes" (puntos del mes), "quién viene
 * mejor en el año" (ranking tipo ATP, la suma de los meses) y "quién juega
 * mejor" (Nivel, el Elo). Verlas al lado deja clara la diferencia.
 */
export function Tabla({
  yo,
  solapa,
  setSolapa,
  onVerPerfil,
  onVerResumen,
  sinConfirmar,
}: {
  yo: Yo;
  // La solapa vive en App: asi, al volver de un perfil, se vuelve a la misma.
  solapa: Solapa;
  setSolapa: (s: Solapa) => void;
  onVerPerfil: (id: number) => void;
  onVerResumen: (mes: string) => void;
  /** Pendientes de todo el grupo: ya cuentan, pero todavia se pueden rechazar. */
  sinConfirmar: number;
}) {
  const verRanking = veRanking(yo.jugador.rol);

  return (
    <div className="flex flex-col gap-4">
      {sinConfirmar > 0 && (
        <Aviso tono="alerta">
          {sinConfirmar === 1
            ? 'Hay un partido sin confirmar. Ya cuenta en la tabla,'
            : `Hay ${sinConfirmar} partidos sin confirmar. Ya cuentan en la tabla,`}{' '}
          pero si el equipo rival lo rechaza, sale y la tabla se rehace. Si nadie dice nada, se
          confirma solo en {plazoAutoconfirmar()}.
        </Aviso>
      )}

      <nav className="flex gap-1 rounded-xl border border-(--color-borde) bg-(--color-panel) p-1">
        {(
          [
            ['mes', 'Este mes'],
            ['puntos', 'Ranking'],
            // El Elo: mide nivel (contra quien ganaste), no resultados.
            ['general', 'Nivel'],
            ['parejas', 'Parejas'],
          ] as const
        )
          // Ranking deshabilitado (no paso la votacion): ver veRanking en nucleo/puntos.ts.
          .filter(([id]) => id !== 'puntos' || verRanking)
          .map(([id, texto]) => (
          <button
            key={id}
            onClick={() => setSolapa(id)}
            className={`min-h-10 flex-1 rounded-lg text-sm transition
              ${
                solapa === id
                  ? 'bg-(--color-acento) font-semibold text-(--color-sobre-acento)'
                  : 'text-(--color-apagado)'
              }`}
          >
            {texto}
          </button>
        ))}
      </nav>

      {solapa === 'puntos' && verRanking && <Puntos yo={yo} onVerPerfil={onVerPerfil} />}
      {solapa === 'general' && <General yo={yo} onVerPerfil={onVerPerfil} />}
      {solapa === 'mes' && <Mes yo={yo} onVerPerfil={onVerPerfil} onVerResumen={onVerResumen} />}
      {solapa === 'parejas' && <Parejas yo={yo} onVerPerfil={onVerPerfil} />}
    </div>
  );
}

function General({ yo, onVerPerfil }: { yo: Yo; onVerPerfil: (id: number) => void }) {
  const [filas, setFilas] = useState<FilaRanking[] | null>(null);
  const [el1, setEl1] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .ranking()
      .then((r) => {
        setFilas(r.ranking);
        setEl1(r.el_1);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
  }, []);

  if (error) return <Aviso tono="error">{error}</Aviso>;
  if (!filas) return <Cargando que="Calculando" />;

  const jugaronAlgo = filas.some((f) => f.partidos > 0);
  const enCarrera = filas.filter((f) => f.en_carrera);
  const afuera = filas.filter((f) => !f.en_carrera);

  const renglon = (fila: FilaRanking) => (
    <tr
      key={fila.id}
      onClick={() => onVerPerfil(fila.id)}
      className={`cursor-pointer border-b border-(--color-borde)/40 last:border-0
        active:bg-white/10 ${fila.id === yo.jugador.id ? 'bg-white/5' : ''}
        ${fila.en_carrera ? '' : 'text-(--color-apagado)'}`}
    >
      {fila.en_carrera && (
        <td className="py-2 pl-3 font-titulo text-2xl tabular-nums">{fila.posicion}</td>
      )}
      <td
        className={`py-3 underline decoration-(--color-borde) underline-offset-2
          ${fila.en_carrera ? '' : 'pl-3'}`}
      >
        {fila.nombre}
        {fila.id === el1 && <CoronaEl1 />}
        {fila.provisorio && fila.partidos > 0 && (
          <span
            className="ml-2 text-[10px] text-(--color-apagado)"
            title="Todavía jugó pocos partidos: el nivel se va a acomodar"
          >
            provisorio
          </span>
        )}
      </td>
      <td className="py-3 text-right tabular-nums text-(--color-apagado)">{fila.partidos}</td>
      <td className="py-3 text-right tabular-nums text-(--color-apagado)">
        {fila.ganados}-{fila.perdidos}
      </td>
      <td className="py-3 pr-3 text-right font-semibold tabular-nums">{fila.elo}</td>
    </tr>
  );

  return (
    <div className="flex flex-col gap-4">
      <Titulo>Nivel</Titulo>

      {!jugaronAlgo ? (
        <Vacio>
          Todavía no hay partidos. Cuando se cargue el primero, la tabla arranca a moverse.
        </Vacio>
      ) : (
        <>
          {enCarrera.length === 0 ? (
            <Vacio>Nadie jugó en los últimos {DIAS_EN_CARRERA} días.</Vacio>
          ) : (
            <TablaDe filas={enCarrera} renglon={renglon} conPuesto />
          )}

          {/* Los que no vienen hace un mes van en su propia tabla, fuera de la
              general: conservan su Elo, pero sin puesto, asi no se lee como
              que bajaron. Vuelven solos a la general con el proximo partido. */}
          {afuera.length > 0 && (
            <>
              <Titulo chico>Sin partidos en los últimos {DIAS_EN_CARRERA} días</Titulo>
              <TablaDe filas={afuera} renglon={renglon} />
            </>
          )}
        </>
      )}

      <Aviso>
        El nivel se calcula como en ajedrez: ganarle a alguien mejor suma más que ganarle a alguien peor,
        y todos arrancaron en 1000. Dice <em>provisorio</em> hasta los 8 partidos, porque antes de
        eso el número todavía se está acomodando. Los que no jugaron en los últimos{' '}
        {DIAS_EN_CARRERA} días salen de la tabla y van a la de abajo, con su nivel
        intacto: vuelven a su lugar con el próximo partido. Tocá un nombre para ver su perfil.
      </Aviso>
    </div>
  );
}

function TablaDe({
  filas,
  renglon,
  conPuesto,
}: {
  filas: FilaRanking[];
  renglon: (fila: FilaRanking) => ReactNode;
  conPuesto?: boolean;
}) {
  return (
    <Tarjeta sinRelleno>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-(--color-heroe) text-[11px] uppercase tracking-wide">
            {conPuesto && <th className="py-2 pl-3 text-left font-normal">#</th>}
            <th className={`py-2 text-left font-normal ${conPuesto ? '' : 'pl-3'}`}>Jugador</th>
            <th className="py-2 text-right font-normal">PJ</th>
            <th className="py-2 text-right font-normal">G-P</th>
            <th className="py-2 pr-3 text-right font-normal">Nivel</th>
          </tr>
        </thead>
        <tbody>{filas.map(renglon)}</tbody>
      </table>
    </Tarjeta>
  );
}

/**
 * El ranking tipo ATP: cada mes cerrado es un torneo que reparte puntos por
 * puesto, y suman los últimos 12. La cuenta está en nucleo/puntos.ts.
 */
function Puntos({ yo, onVerPerfil }: { yo: Yo; onVerPerfil: (id: number) => void }) {
  const [datos, setDatos] = useState<RankingPuntos | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .puntos()
      .then(setDatos)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
  }, []);

  if (error) return <Aviso tono="error">{error}</Aviso>;
  if (!datos) return <Cargando que="Sumando" />;

  const hayCerrados = datos.meses_que_cuentan.length > 0;
  const enJuego = datos.meses_en_juego.map(mesEnPalabras).join(' y ');

  return (
    <div className="flex flex-col gap-4">
      <Titulo>Ranking</Titulo>

      {!RANKING_PUBLICO && (
        <Aviso tono="alerta">
          Esta solapa la ves solo vos (admin). El grupo no la ve hasta que se publique.
        </Aviso>
      )}

      {!hayCerrados && datos.filas.length > 0 && (
        <Aviso tono="alerta">
          Todavía no cerró ningún mes: los puntos se reparten al cierre. Mientras tanto, la
          columna <em>En juego</em> muestra lo que sumaría cada uno si {enJuego} cerrara hoy.
        </Aviso>
      )}

      {datos.filas.length === 0 ? (
        <Vacio>Todavía no hay partidos. El ranking arranca con el primer mes que cierre.</Vacio>
      ) : (
        <Tarjeta sinRelleno>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-(--color-heroe) text-[11px] uppercase tracking-wide">
                <th className="py-2 pl-3 text-left font-normal">#</th>
                <th className="py-2 text-left font-normal">Jugador</th>
                {datos.meses_en_juego.length > 0 && (
                  <th className="py-2 text-right font-normal">En juego</th>
                )}
                <th className="py-2 pr-3 text-right font-normal">Puntos</th>
              </tr>
            </thead>
            <tbody>
              {datos.filas.map((f) => (
                <tr
                  key={f.id}
                  onClick={() => onVerPerfil(f.id)}
                  className={`cursor-pointer border-b border-(--color-borde)/40 last:border-0
                    active:bg-white/10 ${f.id === yo.jugador.id ? 'bg-white/5' : ''}`}
                >
                  <td className="py-2 pl-3 font-titulo text-2xl tabular-nums">
                    {f.posicion ?? '–'}
                  </td>
                  <td className="py-3 underline decoration-(--color-borde) underline-offset-2">
                    {f.nombre}
                    {f.id === datos.el_1 && <CoronaEl1 />}
                  </td>
                  {datos.meses_en_juego.length > 0 && (
                    <td className="py-3 text-right tabular-nums text-(--color-apagado)">
                      {f.en_juego > 0 ? `+${f.en_juego}` : ''}
                    </td>
                  )}
                  <td className="py-3 pr-3 text-right font-semibold tabular-nums">{f.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tarjeta>
      )}

      <Aviso>
        Como el ranking del ATP: <strong>cada mes es un torneo</strong> y reparte puntos según
        el puesto en la tabla del mes — {ESCALA[0]} al 1º, {ESCALA[1]} al 2º, {ESCALA[2]} al
        3º, {ESCALA[3]} al 4º, {ESCALA[4]} del 5º al 8º, {ESCALA[8]} del 9º al 12º y{' '}
        {PUNTOS_RESTO} al resto. Los empatados en puntos se reparten el promedio. El que no llegó
        al mínimo de partidos del mes se lleva {PUNTOS_RESTO}. Suman los últimos{' '}
        {MESES_QUE_CUENTAN} meses cerrados: cuando cierra un mes, se caen los puntos del mismo mes
        del año anterior. <em>En juego</em> es lo que se sumaría si el mes cerrara hoy.
      </Aviso>
    </div>
  );
}
